import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { FileText, Stethoscope, Settings, Lock, TrendingUp, CheckCircle2 } from "lucide-react";

// Icon mapping function
const getIconComponent = (iconName, color) => {
  const iconProps = { size: 24, color };
  const icons = {
    file: <FileText {...iconProps} />,
    stethoscope: <Stethoscope {...iconProps} />,
    settings: <Settings {...iconProps} />,
    lock: <Lock {...iconProps} />,
    trending: <TrendingUp {...iconProps} />,
    check: <CheckCircle2 {...iconProps} />,
  };
  return icons[iconName] || icons.file;
};
  
const solutionsList = [
  {
    titleEn: "For Clinics",
    titleFr: "Pour les Cliniques",
    titleAr: "للعيادات",
    accent: "#10B981",
    accentLight: "#F0FDF4",
    iconName: "file",
    problemEn: "Managing patients, appointments, and staff manually leads to confusion, delays, and errors.",
    problemFr: "La gestion manuelle des patients, des rendez-vous et du personnel entraîne de la confusion, des retards et des erreurs.",
    problemAr: "إدارة المرضى والمواعيد والموظفين يدويًا تؤدي إلى الالتباس والتأخيرات والأخطاء.",
    descEn: "Raqib centralizes all clinic operations into one system, allowing you to manage patients, appointments, and staff efficiently without chaos.",
    descFr: "Raqib centralise toutes les opérations cliniques dans un seul système, vous permettant de gérer les patients, les rendez-vous et le personnel efficacement sans chaos.",
    descAr: "يوحد Raqib جميع عمليات العيادة في نظام واحد، مما يسمح لك بإدارة المرضى والمواعيد والموظفين بكفاءة بدون فوضى.",
    benefitsEn: [
      "Eliminate spreadsheets and paper-based systems",
      "Reduce administrative overhead by 60%",
      "Improve operational efficiency and staff productivity"
    ],
    benefitsFr: [
      "Éliminer les feuilles de calcul et les systèmes papier",
      "Réduire les frais généraux administratifs de 60%",
      "Améliorer l'efficacité opérationnelle et la productivité du personnel"
    ],
    benefitsAr: [
      "القضاء على جداول البيانات والأنظمة الورقية",
      "تقليل النفقات الإدارية بنسبة 60%",
      "تحسين الكفاءة التشغيلية وإنتاجية الموظفين"
    ]
  },
  {
    titleEn: "For Doctors",
    titleFr: "Pour les Médecins",
    titleAr: "للأطباء",
    accent: "#3B82F6",
    accentLight: "#EFF6FF",
    iconName: "stethoscope",
    problemEn: "Doctors need quick access to patient history and appointment details without wasting time.",
    problemFr: "Les médecins ont besoin d'un accès rapide aux antécédents des patients et aux détails des rendez-vous sans perdre de temps.",
    problemAr: "يحتاج الأطباء إلى وصول سريع إلى التاريخ الطبي للمريض وتفاصيل المواعيد دون معدل الوقت.",
    descEn: "Access complete patient information instantly, helping you make faster and more accurate medical decisions.",
    descFr: "Accédez instantanément aux informations complètes du patient, vous aidant à prendre des décisions médicales plus rapides et plus précises.",
    descAr: "الوصول الفوري إلى معلومات المريض الكاملة، مما يساعدك على اتخاذ قرارات طبية أسرع وأكثر دقة.",
    benefitsEn: [
      "View complete medical history at the point of care",
      "Reduce diagnosis time and improve patient outcomes",
      "Better coordination with clinic staff and specialists"
    ],
    benefitsFr: [
      "Afficher l'historique médical complet au point de soin",
      "Réduire le temps de diagnostic et améliorer les résultats des patients",
      "Meilleure coordination avec le personnel de la clinique et les spécialistes"
    ],
    benefitsAr: [
      "عرض السجل الطبي الكامل في نقطة الرعاية",
      "تقليل وقت التشخيص وتحسين نتائج المريض",
      "تنسيق أفضل مع موظفي العيادة والمتخصصين"
    ]
  },
  {
    titleEn: "For Administrators",
    titleFr: "Pour les Administrateurs",
    titleAr: "للمسؤولين",
    accent: "#F59E0B",
    accentLight: "#FFFBEB",
    iconName: "settings",
    problemEn: "Tracking operations, managing schedules, and maintaining order is complex and time-consuming.",
    problemFr: "Le suivi des opérations, la gestion des horaires et le maintien de l'ordre est complexe et chronophage.",
    problemAr: "تتبع العمليات وإدارة الجداول والحفاظ على النظام معقد ويستغرق وقتًا طويلاً.",
    descEn: "Raqib simplifies administrative tasks with structured scheduling and clear appointment tracking.",
    descFr: "Raqib simplifie les tâches administratives avec une planification structurée et un suivi clair des rendez-vous.",
    descAr: "يبسط Raqib المهام الإدارية من خلال الجدولة المنظمة وتتبع المواعيد الواضح.",
    benefitsEn: [
      "Automate scheduling and reduce booking errors",
      "Real-time visibility into clinic operations",
      "Generate reports and analytics for better planning"
    ],
    benefitsFr: [
      "Automatiser la planification et réduire les erreurs de réservation",
      "Visibilité en temps réel sur les opérations de la clinique",
      "Générer des rapports et des analyses pour une meilleure planification"
    ],
    benefitsAr: [
      "أتمتة الجدولة وتقليل أخطاء الحجز",
      "رؤية فوري على عمليات العيادة",
      "إنشاء التقارير والتحليلات للتخطيط الأفضل"
    ]
  },
  {
    titleEn: "For Security & Compliance",
    titleFr: "Pour la Sécurité et la Conformité",
    titleAr: "للأمان والامتثال",
    accent: "#EF4444",
    accentLight: "#FEF2F2",
    iconName: "lock",
    problemEn: "Healthcare data is sensitive and must be protected from unauthorized access.",
    problemFr: "Les données de santé sont sensibles et doivent être protégées contre les accès non autorisés.",
    problemAr: "بيانات الرعاية الصحية حساسة ويجب حمايتها من الوصول غير المرخص.",
    descEn: "Raqib ensures maximum security with multi-layer authentication and full session control, keeping sensitive data protected at all times.",
    descFr: "Raqib assure la sécurité maximale avec une authentification multicouche et un contrôle de session complet, gardant les données sensibles protégées en permanence.",
    descAr: "يضمن Raqib الأمان الأقصى من خلال المصادقة متعددة الطبقات والتحكم الكامل في الجلسة، مما يحافظ على حماية البيانات الحساسة في جميع الأوقات.",
    benefitsEn: [
      "Comply with healthcare data protection regulations",
      "Audit trails for all data access and modifications",
      "Role-based permissions ensure data confidentiality"
    ],
    benefitsFr: [
      "Se conformer aux réglementations de protection des données de santé",
      "Pistes d'audit pour tous les accès et modifications de données",
      "Les autorisations basées sur les rôles assurent la confidentialité des données"
    ],
    benefitsAr: [
      "الامتثال لأنظمة حماية بيانات الرعاية الصحية",
      "مسارات التدقيق لجميع عمليات الوصول وتعديل البيانات",
      "تضمن الأذونات القائمة على الأدوار سرية البيانات"
    ]
  },
  {
    titleEn: "For Growing Clinics",
    titleFr: "Pour les Cliniques en Croissance",
    titleAr: "للعيادات المتنامية",
    accent: "#8B5CF6",
    accentLight: "#FAF5FF",
    iconName: "trending",
    problemEn: "As clinics grow, managing more patients and data becomes harder.",
    problemFr: "Au fur et à mesure que les cliniques se développent, la gestion de plus de patients et de données devient plus difficile.",
    problemAr: "مع نمو العيادات، تصبح إدارة المزيد من المرضى والبيانات أصعب.",
    descEn: "Raqib is built to scale with your clinic, handling increasing data and users without losing performance.",
    descFr: "Raqib est construit pour se développer avec votre clinique, gérant des données et des utilisateurs croissants sans perdre ses performances.",
    descAr: "تم بناء Raqib ليتوسع مع عيادتك، معالجة البيانات والمستخدمين المتزايدين دون فقدان الأداء.",
    benefitsEn: [
      "Add new doctors and staff members easily",
      "System performance remains stable with growing data",
      "Multi-location support for clinic expansion"
    ],
    benefitsFr: [
      "Ajouter facilement de nouveaux médecins et membres du personnel",
      "Les performances du système restent stables avec la croissance des données",
      "Support multi-site pour l'expansion de la clinique"
    ],
    benefitsAr: [
      "إضافة الأطباء والموظفين الجدد بسهولة",
      "تظل أداء النظام مستقرة مع نمو البيانات",
      "دعم متعدد المواقع لتوسع العيادة"
    ]
  }
];

const whyChooseReasons = [
  { reasonEn: "Designed for real healthcare workflows", reasonFr: "Conçu pour les workflows de santé réels", reasonAr: "مصمم لسير عمل الرعاية الصحية الحقيقي" },
  { reasonEn: "Strong focus on security and control", reasonFr: "Accent mis sur la sécurité et le contrôle", reasonAr: "التركيز القوي على الأمان والتحكم" },
  { reasonEn: "Easy to use for both technical and non-technical users", reasonFr: "Facile à utiliser pour les utilisateurs techniques et non techniques", reasonAr: "سهل الاستخدام لكل من المستخدمين التقنيين وغير التقنيين" },
  { reasonEn: "Scales as your clinic grows", reasonFr: "Évolutif au fur et à mesure que votre clinique se développe", reasonAr: "يتسع مع نمو عيادتك" },
];

const FadeUp = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};



const SolutionCard = ({ solution, index, language }) => {
  const [expanded, setExpanded] = React.useState(false);
  const title = language === "ar" ? solution.titleAr : language === "fr" ? solution.titleFr : solution.titleEn;
  const problem = language === "ar" ? solution.problemAr : language === "fr" ? solution.problemFr : solution.problemEn;
  const description = language === "ar" ? solution.descAr : language === "fr" ? solution.descFr : solution.descEn;
  const benefits = language === "ar" ? solution.benefitsAr : language === "fr" ? solution.benefitsFr : solution.benefitsEn;
  const problemLabel = language === "ar" ? "المشكلة" : language === "fr" ? "Problème" : "Problem";
  const solutionLabel = language === "ar" ? "الحل" : language === "fr" ? "Solution" : "Solution";
  const learnMoreText = language === "ar" ? "تعرف على المزيد" : language === "fr" ? "En savoir plus" : "Learn More";
  const showLessText = language === "ar" ? "أظهر أقل" : language === "fr" ? "Afficher moins" : "Show Less";

  return (
    <FadeUp delay={index * 0.1}>
      <motion.div
        whileHover={{ y: -8, boxShadow: `0 20px 40px ${solution.accent}20` }}
        className="h-full p-6 sm:p-8 rounded-3xl border transition-all flex flex-col"
        style={{
          background: solution.accentLight,
          borderColor: solution.accent + "30"
        }}
      >
        <motion.div
          whileHover={{ rotate: [0, -8, 8, 0], transition: { duration: 0.4 } }}
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center mb-5 shadow-md flex-shrink-0"
          style={{ background: "white", color: solution.accent, border: `2px solid ${solution.accent}` }}
        >
          {getIconComponent(solution.iconName, solution.accent)}
        </motion.div>

        <h3
          className="text-xl sm:text-2xl font-black mb-4 tracking-tight"
          style={{ color: solution.accent }}
        >
          {title}
        </h3>

        <div className="mb-5 flex-1">
          <p className="text-xs font-bold mb-2" style={{ color: solution.accent }}>
            {problemLabel}
          </p>
          <p className="text-slate-600 text-sm leading-relaxed font-medium">
            {problem}
          </p>
        </div>

        <div className="mb-6 flex-1">
          <p className="text-xs font-bold mb-2" style={{ color: solution.accent }}>
            {solutionLabel}
          </p>
          <p className="text-slate-600 text-sm leading-relaxed font-light">
            {description}
          </p>
        </div>

        {expanded && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="mb-4 pt-4 border-t border-slate-300 border-opacity-50"
          >
            <p className="text-xs font-bold mb-3" style={{ color: solution.accent }}>
              {language === "ar" ? "الفوائد الرئيسية" : language === "fr" ? "Avantages clés" : "Key Benefits"}
            </p>
            <ul className="space-y-2">
              {benefits.map((benefit, idx) => (
                <motion.li
                  key={idx}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="text-slate-600 text-sm leading-relaxed font-light flex gap-2"
                >
                  <span style={{ color: solution.accent }} className="flex-shrink-0">✓</span>
                  <span>{benefit}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}

        <motion.button
          onClick={() => setExpanded(!expanded)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="w-full py-2.5 rounded-lg font-semibold text-white transition-all text-sm flex-shrink-0"
          style={{ background: solution.accent }}
        >
          {expanded ? showLessText : learnMoreText}
        </motion.button>
      </motion.div>
    </FadeUp>
  );
};

function Solutions() {
  const language = useSelector((state) => state.language);
  const isAr = language === "ar";

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div
      className="min-h-screen bg-white text-gray-900"
      dir={isAr ? "rtl" : "ltr"}
      style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,700;0,900;1,400&family=DM+Serif+Display&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>

      {/* ─── Hero ─── */}
      <section
        ref={heroRef}
        className="relative overflow-hidden min-h-[80vh] sm:min-h-screen flex items-center justify-center px-4 py-24"
        style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)" }}
      >
        {/* Animated background blobs */}
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [0.18, 0.28, 0.18] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, #10B981 0%, transparent 70%)" }}
        />
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.12, 0.22, 0.12] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute bottom-[-15%] right-[-5%] w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, #6366F1 0%, transparent 70%)" }}
        />
        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative z-10 text-center max-w-4xl mx-auto">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block text-xs font-black tracking-[0.25em] uppercase px-4 py-2 rounded-full mb-8 border"
            style={{ color: "#10B981", borderColor: "#10B98140", background: "#10B98110" }}
          >
            {language === "ar" ? "حلولنا" : language === "fr" ? "Nos Solutions" : "Our Solutions"}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-[1.05] tracking-tight"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            {language === "ar" ? "راقب يحل" : language === "fr" ? "Raqib Résout" : "Raqib Solves"}
            <br />
            <span style={{ color: "#10B981" }}>
              {language === "ar" ? "المشاكل الحقيقية" : language === "fr" ? "Des Problèmes Réels" : "Real Problems"}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.22 }}
            className="text-base sm:text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed font-light"
          >
            {language === "ar"
              ? "يتكيف الرقيب مع سير عملك - سواء كنت تدير عيادة أو تتعامل مع المرضى أو تضمن أمان النظام. اكتشف كيف يحل الرقيب المشاكل الحقيقية في بيئات الرعاية الصحية."
              : language === "fr"
              ? "Raqib s'adapte à votre flux de travail — que vous gériez une clinique, traitiez des patients ou assuriez la sécurité du système. Découvrez comment Raqib résout les vrais problèmes dans les environnements de soins de santé."
              : "Raqib adapts to your workflow — whether you're managing a clinic, handling patients, or ensuring system security. Discover how Raqib solves real problems in healthcare environments."}
          </motion.p>

          {/* Scrolling indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-16 flex justify-center"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="flex flex-col items-center gap-2 text-slate-500"
            >
              <span className="text-xs tracking-widest uppercase font-semibold">{language === "ar" ? "اسحب" : language === "fr" ? "Défiler" : "Scroll"}</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* ─── Solutions ─── */}
      <section className="py-20 sm:py-28 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
            {solutionsList.map((solution, index) => (
              <SolutionCard key={index} solution={solution} index={index} language={language} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── Why Choose Raqib ─── */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-slate-950">
        <div className="max-w-5xl mx-auto">
          <FadeUp className="text-center mb-14">
            <p className="text-xs font-black tracking-[0.25em] uppercase text-green-400 mb-3">
              {language === "ar" ? "لماذا راقب" : language === "fr" ? "Pourquoi Raqib" : "Why Raqib"}
            </p>
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 tracking-tight"
              style={{ fontFamily: "'DM Serif Display', serif" }}
            >
              {language === "ar" ? "لماذا اختيار Raqib" : language === "fr" ? "Pourquoi Choisir Raqib" : "Why Choose Raqib"}
            </h2>
            <p className="text-slate-400 text-base sm:text-lg">
              {language === "ar" ? "كل ما تحتاجه في حل الرعاية الصحية الواحد" : language === "fr" ? "Tout ce dont vous avez besoin dans une solution de soins de santé." : "Everything you need in one healthcare solution."}
            </p>
          </FadeUp>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {whyChooseReasons.map((reason, i) => {
              const reasonText = isAr ? reason.reasonAr : language === "fr" ? reason.reasonFr : reason.reasonEn;
              return (
                <FadeUp key={i} delay={i * 0.08}>
                  <motion.div
                    whileHover={{ y: -4, boxShadow: "0 16px 40px rgba(16,185,129,0.15)" }}
                    transition={{ duration: 0.25 }}
                    className="flex items-start gap-4 p-5 sm:p-6 rounded-2xl border border-slate-800 bg-slate-900 group cursor-default"
                  >
                    <div className="flex-shrink-0 text-green-400">
                      <CheckCircle2 size={24} />
                    </div>
                    <p className="text-slate-300 font-medium text-sm sm:text-base leading-relaxed group-hover:text-white transition-colors">
                      {reasonText}
                    </p>
                  </motion.div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="relative overflow-hidden py-24 sm:py-32 px-4 text-center bg-slate-950">
        {/* Glow */}
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(ellipse, #10B98120 0%, transparent 70%)" }}
        />

        <div className="relative z-10 max-w-2xl mx-auto">
          <FadeUp>
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-5 tracking-tight"
              style={{ fontFamily: "'DM Serif Display', serif" }}
            >
              {language === "ar"
                ? "هل أنت مستعد لتحويل عيادتك؟"
                : language === "fr"
                ? "Prêt à Transformer Votre Clinique?"
                : "Ready to Transform Your Clinic?"}
            </h2>
            <p className="text-slate-400 text-base sm:text-lg mb-10 font-light">
              {language === "ar"
                ? "سواء كنت تدير عيادة صغيرة أو توسع عملية أكبر، يوفر Raqib لك الأدوات اللازمة للبقاء منظمًا وآمنًا وفعالًا."
                : language === "fr"
                ? "Que vous gériez une petite clinique ou que vous développiez une opération plus importante, Raqib vous donne les outils pour rester organisé, sécurisé et efficace."
                : "Whether you're running a small clinic or scaling a larger operation, Raqib gives you the tools to stay organized, secure, and efficient."}
            </p>

            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="relative inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-base font-black text-white overflow-hidden"
              style={{ background: "linear-gradient(135deg, #10B981, #059669)" }}
            >
              <motion.span
                animate={{ x: ["-100%", "200%"] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "linear", repeatDelay: 1 }}
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                  width: "60%",
                }}
              />
              {language === "ar" ? "ابدأ الآن" : language === "fr" ? "Commencer Maintenant" : "Get Started Now"}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </motion.button>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}

export default Solutions;
